import React, { useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { WorkspaceProvider, useWorkspace } from "@/contexts/WorkspaceContext";
import AuthScreen from "@/pages/AuthScreen";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Workspace from "@/pages/Workspace";
import WorkspaceOnboarding from "@/components/WorkspaceOnboarding";
import SetupWorkspace from "@/pages/SetupWorkspace";
import SuperAdmin from "@/pages/SuperAdmin";
import WorkspaceSelector from "@/pages/WorkspaceSelector";

function initialRoute() {
  // nginx serves index.html for any path, so these are real routes rather than
  // query flags. The token ones stay as they were.
  const path = window.location.pathname.replace(/\/+$/, "");
  const p = new URLSearchParams(window.location.search);
  if (path === "/setup") return { type: "setup", token: p.get("token") };
  if (path === "/admin") return { type: "admin" };
  if (p.get("reset")) return { type: "reset", token: p.get("reset") };
  if (p.get("invite")) return { type: "invite", token: p.get("invite") };
  return null;
}

function Shell() {
  const { user, loading: authLoading } = useAuth();
  const ws = useWorkspace();
  const [route, setRoute] = useState(initialRoute);

  const clearRoute = () => {
    window.history.replaceState({}, "", window.location.pathname);
    setRoute(null);
  };

  // Setup runs before the auth gate on purpose: the person following the link
  // has no account yet — creating one is the whole point of the page.
  if (route?.type === "setup") return <SetupWorkspace token={route.token} />;
  // Token-driven screens (from email links) take priority over the auth gate.
  if (route?.type === "reset") return <ResetPassword token={route.token} onDone={clearRoute} />;
  if (route?.type === "invite") return <AcceptInvite token={route.token} onDone={clearRoute} />;

  if (authLoading || (user && ws?.loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-fg">
        <div className="flex items-center gap-3 text-sm label-tiny">
          <div className="w-2 h-2 bg-brand pulse-amber" />
          pornește qedara chat…
        </div>
      </div>
    );
  }
  if (!user) return <AuthScreen />;
  if (route?.type === "admin") return <SuperAdmin />;
  if (!ws?.workspaces?.length) return <WorkspaceOnboarding />;
  if (ws.needsChoice) return <WorkspaceSelector onPick={ws.switchWorkspace} />;
  return <Workspace />;
}

function AppToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme === "light" ? "light" : "dark"}
      position="bottom-right"
      toastOptions={{ style: { background: "rgb(var(--surface-2))", border: "1px solid rgb(var(--border) / 0.1)", color: "rgb(var(--text))", borderRadius: "2px" } }}
    />
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <WorkspaceProvider>
            <Shell />
            <AppToaster />
          </WorkspaceProvider>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}
